import React, { useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import MapIcon from '@mui/icons-material/Map';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from '../../hooks/useTranslation';
import { myFigureCollectionService } from '../../services/myFigureCollectionService';

function MyFigureCollectionImport() {
  const { t } = useTranslation();
  const [profileUrl, setProfileUrl] = useState(myFigureCollectionService.referenceProfileUrl);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const planButtonDisabled = true;

  async function handlePreview(event) {
    event.preventDefault();
    setError('');
    try {
      setPreviewLoading(true);
      const data = await myFigureCollectionService.previewImport(profileUrl);
      setPreview(data);
    } catch (err) {
      console.error('Failed to preview MFC import:', err);
      setError(err.message);
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
              {t('collection.import.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t('collection.import.subtitle')}
            </Typography>
          </Box>

          <Alert severity="info" icon={<InfoIcon fontSize="small" />}>
            {t('collection.import.disclaimer')}
          </Alert>

          <Card>
            <CardHeader
              title={t('collection.import.form.title')}
              subheader={t('collection.import.form.subtitle')}
            />
            <Divider />
            <CardContent>
              <form onSubmit={handlePreview}>
                <Stack spacing={2}>
                  <TextField
                    label={t('collection.import.form.profileUrl')}
                    value={profileUrl}
                    onChange={(event) => setProfileUrl(event.target.value)}
                    helperText={t('collection.import.form.helper')}
                    fullWidth
                  />
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      startIcon={<CloudDownloadIcon />}
                      disabled={previewLoading}
                    >
                      {previewLoading ? t('collection.import.actions.loadingPreview') : t('collection.import.actions.preview')}
                    </Button>
                    <Button
                      type="button"
                      variant="outlined"
                      startIcon={<MapIcon />}
                      disabled={planButtonDisabled}
                    >
                      {t('collection.import.actions.planDisabled')}
                    </Button>
                  </Stack>
                </Stack>
              </form>
            </CardContent>
          </Card>

          {error && (
            <Alert severity="error">{error}</Alert>
          )}

          {preview && (
            <Card>
              <CardHeader
                title={t('collection.import.preview.title', { username: preview.username })}
                subheader={preview.profile.url}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Stack spacing={2} alignItems="center">
                      <Avatar
                        src={preview.profile.avatarUrl}
                        alt={preview.profile.username}
                        sx={{ width: 96, height: 96 }}
                      />
                      <Typography variant="subtitle1">{preview.profile.displayName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {preview.profile.location}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} sm={8}>
                    <Stack spacing={2}>
                      <Typography variant="subtitle2" color="text.secondary">
                        {t('collection.import.preview.lastSynced', { timestamp: preview.summary.lastSyncedLabel })}
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Chip color="primary" label={t('collection.import.preview.owned', { count: preview.summary.owned })} />
                        <Chip color="secondary" label={t('collection.import.preview.ordered', { count: preview.summary.ordered })} />
                        <Chip color="info" label={t('collection.import.preview.wished', { count: preview.summary.wished })} />
                        <Chip label={t('collection.import.preview.listed', { count: preview.summary.listedForSale })} />
                      </Stack>
                      {preview.segments.length > 0 && (
                        <>
                          <Divider />
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {preview.segments.map((segment) => (
                              <Chip key={segment.key} variant="outlined" label={`${segment.label}: ${segment.count}`} />
                            ))}
                          </Stack>
                        </>
                      )}
                    </Stack>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('collection.import.preview.sampleItems')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('collection.import.preview.sampleHint')}
                </Typography>

                {preview.sampleItems.length === 0 ? (
                  <Alert severity="info">{t('collection.import.preview.emptyFeeds')}</Alert>
                ) : (
                  <List>
                    {preview.sampleItems.map((item) => (
                      <ListItem key={item.externalId} alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar
                            variant="rounded"
                            src={item.imageUrl}
                            alt={item.name}
                            sx={{ width: 56, height: 56, mr: 1 }}
                          />
                        </ListItemAvatar>
                        <ListItemText
                          primary={item.name}
                          secondary={
                            <>
                              <Typography component="span" variant="body2" color="text.primary">
                                {item.brand}
                              </Typography>
                              {` • ${item.series} • ${item.type}`}
                              <br />
                              {t('collection.import.preview.itemMeta', {
                                scale: item.scale,
                                code: item.productCode,
                                release: item.releaseDate,
                                status: item.status
                              })}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          )}

          <Alert severity="warning">
            {t('collection.import.plan.disabled')}
          </Alert>
        </Stack>
      </Container>
    </Box>
  );
}

export default MyFigureCollectionImport;
